import React, { useState, useRef } from 'react';
import { useAppSettings } from '../../context/AppSettingsContext';

const NOTIF_META = {
  new_booking:           { icon:'fa-calendar-check',   bg:'bg-blue-100',   color:'text-blue-600',   emoji:'📅' },
  booking_cancelled:     { icon:'fa-calendar-xmark',   bg:'bg-red-100',    color:'text-red-600',    emoji:'❌' },
  payment_released:      { icon:'fa-indian-rupee-sign', bg:'bg-green-100',  color:'text-green-600',  emoji:'💰' },
  new_review:            { icon:'fa-star',              bg:'bg-amber-100',  color:'text-amber-600',  emoji:'⭐' },
  qr_scanned:            { icon:'fa-qrcode',            bg:'bg-purple-100', color:'text-purple-600', emoji:'📲' },
  slot_delay:            { icon:'fa-clock',             bg:'bg-orange-100', color:'text-orange-600', emoji:'⏰' },
  appointment_completed: { icon:'fa-circle-check',      bg:'bg-teal-100',   color:'text-teal-600',   emoji:'✅' },
  new_message:           { icon:'fa-comment-dots',      bg:'bg-sky-100',    color:'text-sky-600',    emoji:'💬' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)     return 'just now';
  if (diff < 3600)   return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

// ── Swipeable notification row ─────────────────────────────────────────────
function NotifRow({ notif, onDelete, onTap }) {
  const meta        = NOTIF_META[notif.type] || NOTIF_META.new_booking;
  const touchStartX = useRef(null);
  const [offset, setOffset]       = useState(0);
  const [deleting, setDeleting]   = useState(false);
  const [showDel, setShowDel]     = useState(false);

  // Touch swipe left to reveal delete
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchMove  = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    if (dx < 0) setOffset(Math.max(dx, -80));
  };
  const onTouchEnd = () => {
    if (offset < -40) setShowDel(true);
    else setShowDel(false);
    setOffset(0);
    touchStartX.current = null;
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    await onDelete(notif._id);
  };

  if (deleting) return null;

  return (
    <div className="relative overflow-hidden border-b border-border last:border-0"
      style={{ transition: 'height 0.2s' }}>

      {/* Delete button revealed on swipe */}
      <div className="absolute right-0 top-0 bottom-0 flex items-center bg-red-500 px-4"
        style={{ opacity: showDel ? 1 : 0, transition: 'opacity 0.15s', pointerEvents: showDel ? 'auto' : 'none' }}>
        <button onClick={handleDelete} className="text-white text-sm font-bold flex flex-col items-center gap-1">
          <i className="fa-solid fa-trash text-base" />
          <span className="text-[10px]">Delete</span>
        </button>
      </div>

      {/* Notification content — slides left on swipe */}
      <div
        className={`flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface cursor-pointer ${!notif.isRead ? 'bg-blue-50/40' : 'bg-white'}`}
        style={{ transform: showDel ? 'translateX(-80px)' : `translateX(${offset}px)`, transition: offset === 0 ? 'transform 0.2s' : 'none' }}
        onClick={() => { setShowDel(false); onTap(notif); }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <i className={`fa-solid ${meta.icon} ${meta.color} text-sm`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-text-primary leading-snug">{notif.title}</p>
          <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{notif.message}</p>
          <p className="text-[10px] text-text-muted mt-0.5">{timeAgo(notif.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!notif.isRead && <div className="w-2 h-2 bg-primary rounded-full" />}
          {/* × delete button — always visible on desktop */}
          <button
            onClick={handleDelete}
            className="w-6 h-6 rounded-full hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex"
            title="Delete">
            <i className="fa-solid fa-xmark text-text-muted hover:text-danger text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Notification Detail Popup ──────────────────────────────────────────────
function NotifDetailPopup({ notif, onClose, onDelete }) {
  const settings  = useAppSettings();
  const meta      = NOTIF_META[notif.type] || NOTIF_META.new_booking;
  const logoSrc   = settings.appLogo || '/logo.png';
  const appName   = settings.appName || 'Faristaa';
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(notif._id);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[71] max-w-xs mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="relative bg-gradient-to-br from-primary via-primary to-teal-600 px-5 py-6 text-center">
          <button onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 bg-white/25 hover:bg-white/40 rounded-full flex items-center justify-center">
            <i className="fa-solid fa-xmark text-white text-sm" />
          </button>
          <div className="flex justify-center mb-4">
            <img src={logoSrc} alt={appName}
              className="h-9 w-auto object-contain filter brightness-0 invert"
              onError={e => { e.target.style.display='none'; }} />
          </div>
          <div className={`w-16 h-16 ${meta.bg} rounded-2xl flex items-center justify-center mx-auto shadow-lg`}>
            <i className={`fa-solid ${meta.icon} ${meta.color} text-2xl`} />
          </div>
          <div className="text-3xl mt-2">{meta.emoji}</div>
        </div>
        <div className="px-5 py-5 text-center">
          <h3 className="text-base font-black text-text-primary font-heading mb-2">{notif.title}</h3>
          <p className="text-sm text-text-secondary leading-relaxed mb-3">{notif.message}</p>
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <i className="fa-regular fa-clock text-text-muted text-xs" />
            <span className="text-xs text-text-muted">{timeAgo(notif.createdAt)}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 py-3 bg-primary text-white rounded-2xl text-sm font-bold active:scale-95 transition-transform">
              Got it
            </button>
            <button onClick={handleDelete} disabled={deleting}
              className="w-12 h-12 bg-red-50 border border-red-200 text-danger rounded-2xl flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50">
              {deleting
                ? <i className="fa-solid fa-spinner animate-spin text-sm" />
                : <i className="fa-solid fa-trash text-sm" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Confirm clear all dialog ───────────────────────────────────────────────
function ConfirmClearAll({ onConfirm, onCancel }) {
  return (
    <>
      <div className="fixed inset-0 z-[75] bg-black/50" onClick={onCancel} />
      <div className="fixed inset-x-6 top-1/2 -translate-y-1/2 z-[76] max-w-xs mx-auto bg-white rounded-2xl shadow-2xl p-5 text-center">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <i className="fa-solid fa-trash text-danger text-lg" />
        </div>
        <p className="font-bold text-text-primary mb-1">Clear All Notifications?</p>
        <p className="text-sm text-text-muted mb-4">This will permanently delete all your notifications.</p>
        <div className="flex gap-2">
          <button onClick={onCancel}
            className="flex-1 py-2.5 border border-border text-text-secondary rounded-xl text-sm font-semibold">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 py-2.5 bg-danger text-white rounded-xl text-sm font-bold">
            Clear All
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Panel ─────────────────────────────────────────────────────────────
export default function NotificationsPanel({
  notifications = [], onClose, onMarkAllRead, unreadCount = 0,
  onDeleteOne, onDeleteAll,
}) {
  const [detailNotif, setDetailNotif]     = useState(null);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const handleDeleteOne = async (id) => {
    await onDeleteOne?.(id);
    if (detailNotif?._id === id) setDetailNotif(null);
  };

  const handleDeleteAll = async () => {
    setShowConfirmClear(false);
    await onDeleteAll?.();
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-transparent" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-14 right-2 z-50 w-80 max-w-[calc(100vw-16px)] bg-white rounded-2xl shadow-2xl border border-border flex flex-col"
        style={{ maxHeight: 'calc(100vh - 80px)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-bell text-primary text-sm" />
            <span className="text-sm font-bold text-text-primary">Notifications</span>
            {unreadCount > 0 && (
              <span className="w-5 h-5 bg-danger text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} className="text-xs text-primary font-semibold">
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button onClick={() => setShowConfirmClear(true)}
                className="text-xs text-danger font-semibold px-1">
                Clear all
              </button>
            )}
            <button onClick={onClose}
              className="w-7 h-7 rounded-full bg-surface hover:bg-gray-100 flex items-center justify-center transition-colors">
              <i className="fa-solid fa-xmark text-text-secondary text-sm" />
            </button>
          </div>
        </div>

        {/* Mobile swipe hint */}
        {notifications.length > 0 && (
          <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100 flex items-center gap-1.5 sm:hidden flex-shrink-0">
            <i className="fa-solid fa-hand-point-left text-blue-400 text-xs" />
            <p className="text-[10px] text-blue-600">Swipe left on a notification to delete it</p>
          </div>
        )}

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 bg-surface rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="fa-solid fa-bell text-xl text-gray-200" />
              </div>
              <p className="text-sm text-text-secondary font-medium">No notifications</p>
              <p className="text-xs text-text-muted mt-1">You are all caught up!</p>
            </div>
          ) : (
            <div className="group">
              {notifications.map((notif, i) => (
                <NotifRow
                  key={notif._id || i}
                  notif={notif}
                  onDelete={handleDeleteOne}
                  onTap={setDetailNotif}
                />
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-border bg-surface text-center flex-shrink-0">
            <p className="text-[10px] text-text-muted">
              Tap for details · Swipe left or × to delete · {notifications.length} total
            </p>
          </div>
        )}
      </div>

      {detailNotif && (
        <NotifDetailPopup
          notif={detailNotif}
          onClose={() => setDetailNotif(null)}
          onDelete={handleDeleteOne}
        />
      )}

      {showConfirmClear && (
        <ConfirmClearAll
          onConfirm={handleDeleteAll}
          onCancel={() => setShowConfirmClear(false)}
        />
      )}
    </>
  );
}
